// v14at-p4 F4 — Hook voor AutoMeasureZoneBlock om 1 job te volgen.
import { useCallback } from "react";
import { applyMeasurementJob } from "../lib/measurementJobsApi";
import { useMeasurementJobs } from "../state/measurementJobsStore";

export function useZoneMeasurementJob({ projectId, zoneId, address }) {
  const {
    startJob,
    cancelJob,
    markReviewed,
    markApplying,
    markApplied,
    dismissJob,
    consumeFlash,
    findJobByZone,
    asyncEnabled,
  } = useMeasurementJobs();

  const job = findJobByZone(projectId, zoneId);
  const jobId = job?.job_id || null;

  const start = useCallback(async () => {
    if (!asyncEnabled) {
      throw new Error("async_disabled");
    }
    return startJob({ address, projectId, zoneId });
  }, [asyncEnabled, startJob, address, projectId, zoneId]);

  const retry = useCallback(async () => {
    // v14at-p4 F4.4 (Defect H) — Canonical retry-flow: dismiss de oude
    // zone-reference (zodat `findJobByZone` meteen null retourneert)
    // en vuur direct dezelfde `start()`-pad. Geen lokale UI-fake:
    // backend bepaalt of dit een nieuwe job wordt (201) of een
    // deduplicated bestaande (200 + deduplicated:true).
    if (!asyncEnabled) {
      throw new Error("async_disabled");
    }
    if (jobId) {
      dismissJob(jobId);
    }
    return startJob({ address, projectId, zoneId });
  }, [asyncEnabled, startJob, dismissJob, jobId, address, projectId, zoneId]);

  const cancel = useCallback(async () => {
    if (!jobId) return;
    await cancelJob(jobId);
  }, [cancelJob, jobId]);

  const review = useCallback(() => {
    if (!jobId) return;
    markReviewed(jobId);
  }, [markReviewed, jobId]);

  const dismiss = useCallback(() => {
    if (!jobId) return;
    dismissJob(jobId);
  }, [dismissJob, jobId]);

  const apply = useCallback(
    async ({ strategy = "keep", selectedComponentIds = null } = {}) => {
      if (!jobId) return null;
      markApplying(jobId);
      try {
        const payload = {
          project_id: projectId,
          zone_id: zoneId,
          strategy,
        };
        if (selectedComponentIds && selectedComponentIds.length > 0) {
          payload.selected_component_ids = selectedComponentIds;
        }
        const response = await applyMeasurementJob(jobId, payload);
        markApplied(jobId);
        return response;
      } catch (e) {
        // Return exception zodat caller stale/building_conflict kan afhandelen.
        throw e;
      }
    },
    [jobId, projectId, zoneId, markApplying, markApplied]
  );

  return {
    job,
    jobId,
    uiState: job?.ui_state || null,
    backendStatus: job?.backend_status || null,
    isSuperseded: !!job?.is_superseded,
    pollingHealth: job?.polling_health || "ok",
    flashPending: !!job?.flash_pending,
    start,
    retry,
    cancel,
    review,
    dismiss,
    apply,
    consumeFlash: () => (jobId ? consumeFlash(jobId) : null),
    asyncEnabled,
  };
}
